import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="border-b border-oc-hairline">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-9">
              <div className="eyebrow text-oc-coral mb-8">About Operating Class</div>
              <h1 className="hero-headline text-5xl md:text-7xl text-oc-ink max-w-4xl">
                Business lessons from people actually operating businesses.
              </h1>
              <p className="mt-8 font-editorial text-xl md:text-2xl text-oc-ink-soft leading-snug max-w-3xl">
                Operating Class is a business media and practical education platform. We publish articles, interviews, conversations, and frameworks about the real problems involved in building and running organizations.
              </p>
            </div>
            <div className="lg:col-span-3 flex lg:justify-end">
              <img
                src="/oc-mark-512.png"
                alt="Operating Class mark"
                className="w-28 md:w-32 lg:w-40 opacity-95"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY + AUDIENCE */}
      <section className="border-b border-oc-hairline bg-oc-sand">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
            <div>
              <div className="eyebrow text-oc-coral mb-6">Category</div>
              <p className="font-editorial text-2xl md:text-3xl leading-snug text-oc-ink">
                Business media plus practical business education. Not a consultancy. Not a course provider. Not an agency. An independent publication.
              </p>
            </div>
            <div>
              <div className="eyebrow text-oc-coral mb-6">Who it&#39;s for</div>
              <p className="font-editorial text-2xl md:text-3xl leading-snug text-oc-ink">
                Founders, owners, executives, managers, operators, team leaders, and ambitious professionals looking for useful lessons from people with real operating experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN */}
      <section className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-8 max-w-reading">
            <div className="prose-editorial">
              <h2>What Operating Class publishes</h2>
              <p>Practical articles, interviews, conversations, frameworks, and operator perspectives about the problems operators repeatedly have to solve: hiring, cash flow, delegation, culture, pricing, sales, marketing, management, growth, decision-making, operations, and the day-to-day work of making organizations function.</p>
              <p>The topics are not a random collection of business subjects. They are the questions operators keep hitting. We frame the platform around them so a reader can enter through the problem they are trying to solve.</p>

              <h2>What we don&#39;t do</h2>
              <p>We don&#39;t publish influencer content, hustle-culture writing, or manufactured success stories. We don&#39;t run gotcha interviews. We don&#39;t confuse editorial with sponsorship.</p>

              <h2>Why it exists</h2>
              <p>The most valuable operating knowledge is almost never written down. It lives in private conversations, hard experience, and decisions made under pressure. Operating Class is a deliberate attempt to capture that knowledge, organize it, and make it available to current and future operators.</p>

              <h2>How it makes money today</h2>
              <p>Operating Class is currently audience-first. There is no product for readers to buy. We are focused on building a high-quality library of operator knowledge and a serious audience of people who care about it.</p>
              <p>Long-term revenue may come from sponsorship, partnerships, premium educational products, events, memberships, research, or tools. Only paths we are genuinely considering are listed here. If a path is not listed, we are not doing it.</p>
              <p>Sponsored or commercial content, if and when it appears, will always be clearly labeled and handled separately from independent editorial.</p>

              <h2 id="contributor-policy">Contributor payment policy</h2>
              <p>Editorial contributions are <strong>unpaid by default</strong>. If a paid commission is agreed in advance, that is stated in writing before work begins. Operating Class does not charge legitimate editorial contributors for publication.</p>
              <p>Sponsored or commercial content is handled as a separate arrangement and clearly labeled on the site. Editorial independence and commercial relationships stay distinct.</p>

              <h2>What a reader can do today</h2>
              <ul>
                <li>Read the interviews and articles.</li>
                <li><Link to="/newsletter" className="text-oc-blue">Subscribe to the newsletter</Link> for new work.</li>
                <li>Browse by the problem you&#39;re trying to solve.</li>
              </ul>

              <h2>What a contributor can do today</h2>
              <ul>
                <li><Link to="/contribute" className="text-oc-blue">Pitch an article, framework, or interview</Link>.</li>
                <li>Nominate an operator whose experience should be documented.</li>
              </ul>

              <h2>What a partner can do today</h2>
              <ul>
                <li><Link to="/partner" className="text-oc-blue">Explore sponsorship, series partnerships, and newsletter placements</Link>. Editorial independence stays intact.</li>
              </ul>

              <h2 id="quentin-young-role">Editorial voice</h2>
              <p>
                Operating Class is edited by{" "}
                <a
                  href="https://quentinyoung.ca"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="about-quentin-link"
                  className="text-oc-blue"
                >
                  Quentin Young
                </a>. He shapes the editorial direction and interviews most contributors. Operating Class is the enduring publication. Quentin is the person behind the editorial lens.
              </p>

              <h2>What we are building toward</h2>
              <p>The written interview is the first public format. Video, audio, and useful tools follow next. In the longer term, premium educational products, events, and research are on the table. We will only launch what we can do well, and we will separate what exists today from what exists tomorrow.</p>
            </div>
          </div>

          <aside className="lg:col-span-4 space-y-10 lg:sticky lg:top-28 self-start">
            <div className="border-l-2 border-oc-coral pl-6 py-2">
              <div className="eyebrow text-oc-ink-muted mb-3">Read the manifesto</div>
              <p className="font-editorial text-lg text-oc-ink leading-snug">
                The fuller argument for why Operating Class exists.
              </p>
              <Link to="/manifesto" className="mt-4 inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest border-b border-oc-blue text-oc-blue pb-1 hover:text-oc-coral hover:border-oc-coral">
                Read it <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="border-l-2 border-oc-blue pl-6 py-2">
              <div className="eyebrow text-oc-ink-muted mb-3">Contribute</div>
              <p className="font-editorial text-lg text-oc-ink leading-snug">
                Have operating experience worth teaching?
              </p>
              <Link to="/contribute" className="mt-4 inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest border-b border-oc-blue text-oc-blue pb-1 hover:text-oc-coral hover:border-oc-coral">
                Pitch a contribution <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="border-l-2 border-oc-ink pl-6 py-2">
              <div className="eyebrow text-oc-ink-muted mb-3">Partner with us</div>
              <p className="font-editorial text-lg text-oc-ink leading-snug">
                Sponsorships, series partnerships, and newsletter placements.
              </p>
              <Link to="/partner" className="mt-4 inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest border-b border-oc-blue text-oc-blue pb-1 hover:text-oc-coral hover:border-oc-coral">
                See the options <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
